/* eslint-disable class-methods-use-this */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import settings from '../settings';
import { checkIsObject, get, transformApiErrors } from '../utils/helper/helper';
import { emitErrorMsgToaster } from '../components/common/Toaster';

const REQUEST_METHOD = {
  GET: 'GET',
  POST: 'POST',
  PATCH: 'PATCH',
  PUT: 'PUT',
  DELETE: 'DELETE',
} as const;
export const storedAccessToken = () => localStorage.getItem('access') || sessionStorage.getItem('access');
export const storedRefreshToken = () => localStorage.getItem('refresh') || sessionStorage.getItem('refresh');

/**
 * Tokens from before the tenant login carry no tenant_id claim. The backend answers every resource call made with
 * them with 403, and refreshing gives a token without the claim again.
 * A token that can not be read is left to the backend, which answers it with 401.
 */
const hasTenantClaim = (token: string) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return Boolean(payload.tenant_id);
  } catch (e) {
    return true;
  }
};
/**
 * @class
 * @description This service class handles all REST Calls
 */

class HttpService {
  private service: AxiosInstance;

  /**
   * @function
   * @description Init the axios library and register the interceptors.
   */
  constructor() {
    if (!settings.tenantId || !settings.productId) {
      // eslint-disable-next-line no-console
      console.error(
        'REACT_APP_TENANT_ID and REACT_APP_PRODUCT_ID must be set: ' +
          'login and every resource call fail without them.',
      );
    }

    // a session without tenant can not be repaired: drop it, so the user logs in anew
    const storedToken = storedAccessToken();
    if (storedToken && !hasTenantClaim(storedToken)) this.removeAuthToken();

    this.service = axios.create({
      headers: {
        'Content-Type': 'application/json',
      }, // withCredentials: true, // Re-add if you have problems with CORS
    });
    // interceptor needed for HERE Autocomplete API because it does not allow headers
    this.service.interceptors.request.use(
      (config) => {
        const token = storedAccessToken();
        const conf = config;

        if (token) {
          conf.headers.Authorization = `Bearer ${token}`;
          conf.headers['Content-Type'] = 'application/json';
        }

        // Product context: the backend scopes every sim.TLK resource by this header.
        // Endpoints that do not need it ignore it.
        if (settings.productId) {
          conf.headers['X-Context-Id'] = settings.productId;
        }

        return conf;
      },
      (error) => Promise.reject(error),
    );
    this.service.interceptors.response.use(
      (response) => {
        return response;
      }, // intercept here if access token is expired
      (error) => {
        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          get(() => error.response.data.messages[0].token_type) === 'access' &&
          get(() => error.response.data.messages[0].message) === 'Token is invalid or expired'
        ) {
          const refreshToken = storedRefreshToken();
          return this.post(
            'token/refresh/',
            {
              refresh: refreshToken,
            },
            undefined,
          )
            .then((response) => {
              this.setAccessToken(response.data.access);
              this.service.defaults.headers.Authorization = `Bearer ${response.data.access}`;
              originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
              return this.service(originalRequest);
            })
            .catch((err) => {
              if (err.response.status === 401) {
                this.removeAuthToken();

                if (window.location.pathname !== '/') {
                  window.location.href = '/';
                }
              }
            });
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * @function
   * @description Set the access token in the request header. Call this after successful login.
   * @param {string} token is a token string.
   * @param {boolean} stayLoggedIn logged in.
   */
  setAccessToken = (token: string, stayLoggedIn = true) => {
    // this.service.defaults.headers.common.Authorization = token ? `Token ${token}` : '';
    if (stayLoggedIn) {
      localStorage.setItem('access', token);
    } else {
      sessionStorage.setItem('access', token);
    }
  };

  /**
   * @function
   * @description Set the refresh token in the request header. Call this after successful login.
   * @param {string} token is a token string.
   * @param {boolean} stayLoggedIn logged in.
   */
  setRefreshToken = (token: string, stayLoggedIn = true) => {
    // this.service.defaults.headers.common.Authorization = token ? `Token ${token}` : '';
    if (stayLoggedIn) {
      localStorage.setItem('refresh', token);
    } else {
      sessionStorage.setItem('refresh', token);
    }
  };

  /**
   * @function
   * @description Removes the access and refresh token in the header. Call this after logout.
   */
  removeAuthToken = () => {
    // delete this.service.defaults.headers.common.Authorization;
    localStorage.removeItem('access');
    sessionStorage.removeItem('access');
    localStorage.removeItem('refresh');
    sessionStorage.removeItem('refresh');
  };

  /**
   * @function
   * @description Handles unauthorized request, redirects to login
   */
  unauthorizedHandler = () => {
    this.removeAuthToken();

    if (window.location.pathname !== '/') {
      window.location.href = '/';
    }
  };

  /**
   * @description Method to handle the request by method.
   * @param method {string} th request method type.
   * @param {string} path A relative path that comes after the API URL defined in settings.js, e.g. "reports"
   * @param {*} data is an object with API data.
   * @param {*} params is an object with API params.
   * @param {boolean} logError controls whether errorToaster is emitted.
   * @return {Promise<AxiosResponse<any>>}
   */
  handleRequest = (
    method: AxiosRequestConfig['method'],
    path: string,
    logError: boolean,
    version: string,
    data = {},
    params = {},
    responseType: AxiosRequestConfig['responseType'] = 'json',
  ) =>
    this.service
      .request({
        url: `${settings.apiUrl}${version}/${path}`,
        method,
        data,
        params,
        responseType,
      })
      .catch((error) => this.handleError(error, logError));

  /**
   * @function
   * @description Interceptor for the error response
   * @param {AxiosError} error is a object with the error response from API.
   * @param {boolean} logError controls whether errorToaster is emitted.
   */
  handleError = (error: AxiosError, logError: boolean) => {
    // emits ErrorMsgToaster if logError is set to true
    if (logError) {
      // not every failed request has a JSON body: it can be an HTML page (502), or nothing at all (network error)
      const data = error.response?.data;
      emitErrorMsgToaster({
        content: checkIsObject(data) || Array.isArray(data) ? Object.values(data) : [],
      });
    }

    const err = {
      ...error,
      response: { ...error.response, data: transformApiErrors(get(() => error.response?.data)) },
    };

    if (err.response) {
      switch (err.response.status) {
        case 401:
          this.unauthorizedHandler();
          break;

        default:
          break;
      }

      return Promise.reject(err);
    }

    return Promise.reject(err);
  };

  /**
   * @function
   * @description Fires the get request
   * @param {string} path is a string of API path.
   * @param {*} params is an object with API params.
   * @param {*} responseType of the request.
   * @param {boolean} logError controls whether errorToaster is emitted.
   */
  get = (path: string, params: any, responseType: any, logError: boolean = false, version: string = 'v1') =>
    this.handleRequest(REQUEST_METHOD.GET, path, logError, version, {}, params, responseType);

  /**
   * @function
   * @description Fires the patch request
   * @param {string} path A relative path that comes after the API URL defined in settings.js, e.g. "reports"
   * @param {Object} data is an object to submit to API.
   * @param {boolean} logError controls whether errorToaster is emitted.
   */
  patch = (path: string, data: { [index: string]: any }, logError: boolean, version: string = 'v1') =>
    this.handleRequest(REQUEST_METHOD.PATCH, path, logError, version, data, {}, 'json');

  /**
   * @function
   * @description Fires the put request
   * @param {string} path A relative path that comes after the API URL defined in settings.js, e.g. "reports"
   * @param {Object} data is an object to submit to API.
   * @param {boolean} logError controls whether errorToaster is emitted.
   * @return {*}
   */
  put = (path: string, data: { [index: string]: any }, logError: boolean, version: string = 'v1') =>
    this.handleRequest(REQUEST_METHOD.PUT, path, logError, version, data, {}, 'json');

  /**
   * @function
   * @description Fires the post request
   * @param {string} path A relative path that comes after the API URL defined in settings.js, e.g. "reports"
   * @param {Object} data is an object to submit to API.
   * @param {*} params is an object with API params.
   * @param {boolean} logError controls whether errorToaster is emitted.
   * @return {*}
   */
  post = (
    path: string,
    data: { [index: string]: any },
    params?: any,
    logError: boolean = false,
    version: string = 'v1',
  ) => this.handleRequest(REQUEST_METHOD.POST, path, logError, version, data, params, 'json');

  /**
   * @function
   * @description Fires the delete request
   * @param {string} path A relative path that comes after the API URL defined in settings.js, e.g. "reports"
   * @param {Object} data is an object to submit to API.
   * @param {boolean} logError controls whether errorToaster is emitted.
   * @return {*}
   */
  delete = (path: string, data: { [index: string]: any }, logError = false, version: string = 'v1') =>
    this.handleRequest(REQUEST_METHOD.DELETE, path, logError, version, data, {}, 'json');
}

const HttpServiceInstance = new HttpService();
export default HttpServiceInstance;
